import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaTruck, FaTrailer, FaUser, FaPlus, FaTrash, FaEdit,
  FaSearch, FaTimes, FaSave, FaFileAlt, FaSpinner,
  FaIdCard, FaUpload, FaEye
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import ConfirmDialog from '../components/ConfirmDialog';

// Shared fetch helper: prefixes API_URL + attaches the auth token.
// Fixes "html instead of json" errors from bare relative fetches.
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
};

function SubbieFleet({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fleet, setFleet] = useState({ trucks: [], trailers: [], drivers: [], cranes: [] });
  const [subbieId, setSubbieId] = useState(null);
  const [activeTab, setActiveTab] = useState('trucks');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('truck');
  const [formData, setFormData] = useState({});
  const [processing, setProcessing] = useState(false);
  
  // Driver document state
  const [driverDocs, setDriverDocs] = useState({
    id_document: null,
    prdp_document: null,
    license_photo: null
  });
  const [docNames, setDocNames] = useState({
    id_document: '',
    prdp_document: '',
    license_photo: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) {
          setSubbieId(savedId);
          fetchData(savedId);
          return;
        }

        const data = await apiFetch(`/subcontractors?user_id=${user.id}`);
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          fetchData(id);
        } else {
          setError('No subcontractor profile found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching subbie ID:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    };

    if (user.id) {
      getSubbieId();
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/subbie/fleet/${id}`);
      if (data.success) {
        setFleet({
          trucks: data.trucks || [],
          trailers: data.trailers || [],
          drivers: data.drivers || [],
          cranes: data.cranes || []
        });
      } else {
        setError(data.message || 'Failed to load fleet');
      }
    } catch (err) {
      console.error('Error fetching fleet:', err);
      setError('Failed to load fleet');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload JPEG, PNG, or PDF files only');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setDriverDocs(prev => ({ ...prev, [field]: file }));
      setDocNames(prev => ({ ...prev, [field]: file.name }));
    }
  };

  const handleAddFleet = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      let endpoint = `/subbie/fleet/${addType}`;
      let payload = { ...formData, subcontractor_id: subbieId };

      // parseFloat('') is NaN, and JSON.stringify turns NaN into null — which Postgres
      // rejects on a NOT NULL column (that is what stopped trailers being added). Strip
      // NaN to null so an OPTIONAL number left blank never 500s the save.
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'number' && !Number.isFinite(payload[k])) payload[k] = null;
      });

      // For drivers, use FormData to handle file uploads
      if (addType === 'driver') {
        const formDataObj = new FormData();
        Object.keys(formData).forEach(key => {
          if (formData[key] !== undefined && formData[key] !== null) {
            formDataObj.append(key, formData[key]);
          }
        });
        formDataObj.append('subcontractor_id', subbieId);
        
        if (driverDocs.id_document) {
          formDataObj.append('id_document', driverDocs.id_document);
        }
        if (driverDocs.prdp_document) {
          formDataObj.append('prdp_document', driverDocs.prdp_document);
        }
        if (driverDocs.license_photo) {
          formDataObj.append('license_photo', driverDocs.license_photo);
        }

        const data = await apiFetch(endpoint, {
          method: 'POST',
          body: formDataObj
        });
        if (data.success) {
          toast.success('Driver added successfully!');
          setShowAddModal(false);
          setFormData({});
          setDriverDocs({ id_document: null, prdp_document: null, license_photo: null });
          setDocNames({ id_document: '', prdp_document: '', license_photo: '' });
          fetchData(subbieId);
        } else {
          toast.error(data.error || 'Failed to add fleet item');
        }
        setProcessing(false);
        return;
      }

      // For trucks, trailers and cranes (JSON)
      const data = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.success) {
        toast.success(`${addType} added successfully!`);
        setShowAddModal(false);
        setFormData({});
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to add fleet item');
      }
    } catch (err) {
      console.error('Error adding fleet:', err);
      toast.error('Failed to add fleet item');
    } finally {
      setProcessing(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id }

  const handleDelete = (type, id) => {
    setDeleteTarget({ type, id });
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    try {
      const data = await apiFetch(`/subbie/fleet/${deleteTarget.type}/${deleteTarget.id}`, {
        method: 'DELETE'
      });
      if (data.success) {
        toast.success(`${deleteTarget.type} deleted successfully`);
        fetchData(subbieId);
      } else {
        toast.error(data.error || 'Failed to delete');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  const getStatusBadge = (isVerified) => {
    return isVerified 
      ? 'bg-green-100 text-green-600'
      : 'bg-yellow-100 text-yellow-600';
  };

  const getDeleteType = () => {
    if (activeTab === 'trucks') return 'truck';
    if (activeTab === 'trailers') return 'trailer';
    if (activeTab === 'cranes') return 'crane';
    return 'driver';
  };

  const getAddLabel = () => {
    if (activeTab === 'trucks') return 'Truck';
    if (activeTab === 'trailers') return 'Trailer';
    if (activeTab === 'cranes') return 'Crane';
    return 'Driver';
  };

  const getEmptyHint = () => {
    if (activeTab === 'trucks') return 'truck';
    if (activeTab === 'trailers') return 'trailer';
    if (activeTab === 'cranes') return 'crane';
    return 'driver';
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="fleet" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading fleet...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="fleet" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200 max-w-md mx-auto">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Fleet</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchData(subbieId)}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  const currentItems = fleet[activeTab] || [];

  return (
    <SubbieLayout activeTab="fleet" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">🚛 My Fleet</h1>
          <p className="text-gray-500 text-sm">Manage your trucks, trailers, cranes, and drivers</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs text-gray-500">
            <span className="mr-2">🟠 <span className="text-amber-600">expiring &lt;30 days</span></span>
            <span>🔴 <span className="text-red-600">expired</span></span>
          </div>
          <button
            onClick={() => { 
              setAddType(getAddLabel().toLowerCase()); 
              setShowAddModal(true);
              setFormData({});
              setDriverDocs({ id_document: null, prdp_document: null, license_photo: null });
              setDocNames({ id_document: '', prdp_document: '', license_photo: '' });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
          >
            <FaPlus size={14} /> Add {getAddLabel()}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 border border-gray-200">
        <button
          onClick={() => setActiveTab('trucks')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'trucks' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaTruck className="inline mr-2" /> Trucks ({fleet.trucks.length})
        </button>
        <button
          onClick={() => setActiveTab('trailers')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'trailers' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaTrailer className="inline mr-2" /> Trailers ({fleet.trailers.length})
        </button>
        <button
          onClick={() => setActiveTab('cranes')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'cranes' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaTruck className="inline mr-2 transform rotate-45" /> Cranes ({fleet.cranes.length})
        </button>
        <button
          onClick={() => setActiveTab('drivers')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'drivers' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaUser className="inline mr-2" /> Drivers ({fleet.drivers.length})
        </button>
      </div>

      {/* Fleet List */}
      {currentItems.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
          <div className="text-6xl mb-4">🚛</div>
          <h3 className="text-xl font-semibold text-navy mb-2">No {activeTab} Added</h3>
          <p className="text-gray-500">Start by adding your first {getEmptyHint()}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentItems.map((item) => (
            <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  {activeTab === 'trucks' && (
                    <>
                      <h4 className="font-bold text-navy">{item.unit_name || item.registration_number}</h4>
                      <p className="text-sm text-gray-600">{item.make} {item.model}{item.truck_type ? ` · ${item.truck_type.replace(/_/g, ' ')}` : ''}</p>
                      <p className="text-xs text-gray-500">{item.capacity_mt ? `${item.capacity_mt} MT capacity` : ''}</p>
                      <p className="text-xs text-gray-500">Status: {item.current_status || 'Available'}</p>
                    </>
                  )}
                  {activeTab === 'trailers' && (
                    <>
                      <h4 className="font-bold text-navy">{item.trailer_name || item.registration_number}</h4>
                      <p className="text-sm text-gray-600">{item.trailer_type}{item.axle_config ? ` · ${item.axle_config}` : ''}</p>
                      <p className="text-xs text-gray-500">{item.capacity_mt ? `${item.capacity_mt} MT capacity` : ''}</p>
                    </>
                  )}
                  {activeTab === 'cranes' && (
                    <>
                      <h4 className="font-bold text-navy">{item.unit_name || `${item.make || ''} ${item.model || ''}`.trim() || 'Crane'}</h4>
                      <p className="text-sm text-gray-600">{item.crane_type ? item.crane_type.replace(/_/g, ' ') : 'Crane'}</p>
                      <p className="text-xs text-gray-500">
                        {item.max_capacity_tonnes ? `${item.max_capacity_tonnes} t capacity` : ''}
                        {item.boom_length_m ? ` · ${item.boom_length_m} m boom` : ''}
                        {item.max_hook_height_m ? ` · ${item.max_hook_height_m} m hook` : ''}
                      </p>
                    </>
                  )}
                  {activeTab === 'drivers' && (
                    <>
                      <h4 className="font-bold text-navy">{item.full_name}</h4>
                      <p className="text-sm text-gray-600">ID: {item.id_number || 'N/A'}</p>
                      <p className="text-xs text-gray-500">PrDP: {item.prdp_number || 'N/A'}</p>
                      <div className="mt-1 flex gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(item.is_verified)}`}>
                          {item.is_verified ? '✅ Verified' : '⏳ Pending'}
                        </span>
                        {item.id_document_url && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <FaFileAlt size={10} /> ID
                          </span>
                        )}
                        {item.prdp_document_url && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <FaFileAlt size={10} /> PrDP
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(getDeleteType(), item.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <FaTrash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">
                Add New {addType.charAt(0).toUpperCase() + addType.slice(1)}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleAddFleet} className="p-6">
              <div className="space-y-4">
                {addType === 'truck' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Truck Type</label>
                      <select
                        value={formData.truck_type || ''}
                        onChange={(e) => setFormData({ ...formData, truck_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="">Select Type...</option>
                        <option value="prime_mover">Prime Mover</option>
                        <option value="rigid">Rigid</option>
                        <option value="side_lifter">Side Lifter</option>
                        <option value="crane_truck">Crane Truck (Hiab)</option>
                        <option value="tipper">Tipper</option>
                        <option value="tanker">Tanker</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit Name (e.g. TR001)</label>
                      <input
                        type="text"
                        value={formData.unit_name || ''}
                        onChange={(e) => setFormData({ ...formData, unit_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number *</label>
                      <input
                        type="text"
                        value={formData.registration_number || ''}
                        onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                      <input
                        type="text"
                        value={formData.make || ''}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <input
                        type="text"
                        value={formData.model || ''}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (MT)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.capacity_mt || ''}
                        onChange={(e) => setFormData({ ...formData, capacity_mt: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Licence Expiry</label>
                      <input
                        type="date"
                        value={formData.licence_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, licence_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">COF Expiry</label>
                      <input
                        type="date"
                        value={formData.cof_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, cof_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                  </>
                )}
                {addType === 'trailer' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number *</label>
                      <input
                        type="text"
                        value={formData.registration_number || ''}
                        onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trailer Name (e.g. TL001)</label>
                      <input
                        type="text"
                        value={formData.trailer_name || ''}
                        onChange={(e) => setFormData({ ...formData, trailer_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trailer Type</label>
                      <select
                        value={formData.trailer_type || ''}
                        onChange={(e) => setFormData({ ...formData, trailer_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="">Select Type...</option>
                        <option value="side_tipper">Side Tipper</option>
                        <option value="grain_trailer">Grain Trailer</option>
                        <option value="flatbed">Flatbed</option>
                        <option value="flatdeck">Flatdeck</option>
                        <option value="tautliner">Tautliner</option>
                        <option value="lowbed">Lowbed</option>
                        <option value="superlink">Superlink</option>
                        <option value="tanker">Tanker</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Axle Configuration</label>
                      <select
                        value={formData.axle_config || ''}
                        onChange={(e) => setFormData({ ...formData, axle_config: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="">Select...</option>
                        <option value="single">Single</option>
                        <option value="tandem">Tandem</option>
                        <option value="triaxle">Triaxle</option>
                        <option value="multi">Multi</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (MT)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.capacity_mt || ''}
                        onChange={(e) => setFormData({ ...formData, capacity_mt: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Licence Expiry</label>
                      <input
                        type="date"
                        value={formData.licence_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, licence_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">COF Expiry</label>
                      <input
                        type="date"
                        value={formData.cof_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, cof_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                  </>
                )}
                {addType === 'crane' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Crane Type</label>
                      <select
                        value={formData.crane_type || ''}
                        onChange={(e) => setFormData({ ...formData, crane_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      >
                        <option value="">Select Type...</option>
                        <option value="mobile">Mobile Crane</option>
                        <option value="tower">Tower Crane</option>
                        <option value="crawler">Crawler Crane</option>
                        <option value="truck_mounted">Truck-Mounted Crane</option>
                        <option value="all_terrain">All-Terrain Crane</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                      <input
                        type="text"
                        value={formData.make || ''}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <input
                        type="text"
                        value={formData.model || ''}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit / Asset Name (e.g. CR001)</label>
                      <input
                        type="text"
                        value={formData.unit_name || ''}
                        onChange={(e) => setFormData({ ...formData, unit_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity (tonnes)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.max_capacity_tonnes || ''}
                        onChange={(e) => setFormData({ ...formData, max_capacity_tonnes: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Boom Length (m)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.boom_length_m || ''}
                        onChange={(e) => setFormData({ ...formData, boom_length_m: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Hook Height (m)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.max_hook_height_m || ''}
                        onChange={(e) => setFormData({ ...formData, max_hook_height_m: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Counterweight (tonnes, optional)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.counterweight_tonnes || ''}
                        onChange={(e) => setFormData({ ...formData, counterweight_tonnes: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Load Test / Cert Expiry</label>
                      <input
                        type="date"
                        value={formData.load_test_cert_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, load_test_cert_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.notes || ''}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        rows="2"
                      />
                    </div>
                  </>
                )}
                {addType === 'driver' && (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800 flex items-start gap-2">
                        <FaFileAlt className="mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Required:</strong> Upload ID document and PrDP certificate for verification.
                        </span>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={formData.full_name || ''}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID/Passport Number *</label>
                      <input
                        type="text"
                        value={formData.id_number || ''}
                        onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PrDP Number *</label>
                      <input
                        type="text"
                        value={formData.prdp_number || ''}
                        onChange={(e) => setFormData({ ...formData, prdp_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PrDP Expiry Date *</label>
                      <input
                        type="date"
                        value={formData.prdp_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, prdp_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Licence Expiry</label>
                      <input
                        type="date"
                        value={formData.licence_expiry || ''}
                        onChange={(e) => setFormData({ ...formData, licence_expiry: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="text"
                        value={formData.phone || ''}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      />
                    </div>

                    {/* Document Uploads */}
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-semibold text-navy mb-3">Document Uploads</h3>
                      <div className="space-y-3">
                        <div className="border rounded-lg p-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            ID/Passport Document <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'id_document')}
                            className="w-full text-sm"
                            required
                          />
                          {docNames.id_document && (
                            <span className="text-xs text-green-600">✅ {docNames.id_document}</span>
                          )}
                        </div>

                        <div className="border rounded-lg p-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            PrDP Certificate <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'prdp_document')}
                            className="w-full text-sm"
                            required
                          />
                          {docNames.prdp_document && (
                            <span className="text-xs text-green-600">✅ {docNames.prdp_document}</span>
                          )}
                        </div>

                        <div className="border rounded-lg p-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            License Photo (Optional)
                          </label>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'license_photo')}
                            className="w-full text-sm"
                          />
                          {docNames.license_photo && (
                            <span className="text-xs text-green-600">✅ {docNames.license_photo}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.notes || ''}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                        rows="2"
                        placeholder="Any additional notes..."
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
                  {processing ? 'Adding...' : `Add ${addType.charAt(0).toUpperCase() + addType.slice(1)}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete"
        message={deleteTarget ? `Delete this ${deleteTarget.type}?` : 'Delete this item?'}
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </SubbieLayout>
  );
}

export default SubbieFleet;
